package com.tedd.db

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Long
import kotlin.String

public class KeychainQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> selectAll(mapper: (
    id: String,
    name: String,
    icon: String,
    color: String,
    wrapped_key: String,
    kdf: String,
    order_index: Long,
    created_at: Long,
    updated_at: Long,
    is_deleted: Long,
    deleted_at: Long?,
    synced_at: Long?,
  ) -> T): Query<T> = Query(371_197_545, arrayOf("keychain"), driver, "Keychain.sq", "selectAll",
      "SELECT keychain.id, keychain.name, keychain.icon, keychain.color, keychain.wrapped_key, keychain.kdf, keychain.order_index, keychain.created_at, keychain.updated_at, keychain.is_deleted, keychain.deleted_at, keychain.synced_at FROM keychain WHERE is_deleted = 0 ORDER BY order_index, created_at") {
      cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9)!!,
      cursor.getLong(10),
      cursor.getLong(11)
    )
  }

  public fun selectAll(): Query<Keychain> = selectAll { id, name, icon, color, wrapped_key, kdf,
      order_index, created_at, updated_at, is_deleted, deleted_at, synced_at ->
    Keychain(
      id,
      name,
      icon,
      color,
      wrapped_key,
      kdf,
      order_index,
      created_at,
      updated_at,
      is_deleted,
      deleted_at,
      synced_at
    )
  }

  public fun <T : Any> selectByIdAnyState(id: String, mapper: (
    id: String,
    name: String,
    icon: String,
    color: String,
    wrapped_key: String,
    kdf: String,
    order_index: Long,
    created_at: Long,
    updated_at: Long,
    is_deleted: Long,
    deleted_at: Long?,
    synced_at: Long?,
  ) -> T): Query<T> = SelectByIdAnyStateQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9)!!,
      cursor.getLong(10),
      cursor.getLong(11)
    )
  }

  public fun selectByIdAnyState(id: String): Query<Keychain> = selectByIdAnyState(id) { id_, name,
      icon, color, wrapped_key, kdf, order_index, created_at, updated_at, is_deleted, deleted_at,
      synced_at ->
    Keychain(
      id_,
      name,
      icon,
      color,
      wrapped_key,
      kdf,
      order_index,
      created_at,
      updated_at,
      is_deleted,
      deleted_at,
      synced_at
    )
  }

  public fun <T : Any> selectDirty(mapper: (
    id: String,
    name: String,
    icon: String,
    color: String,
    wrapped_key: String,
    kdf: String,
    order_index: Long,
    created_at: Long,
    updated_at: Long,
    is_deleted: Long,
    deleted_at: Long?,
    synced_at: Long?,
  ) -> T): Query<T> = Query(241_245_850, arrayOf("keychain"), driver, "Keychain.sq", "selectDirty",
      "SELECT keychain.id, keychain.name, keychain.icon, keychain.color, keychain.wrapped_key, keychain.kdf, keychain.order_index, keychain.created_at, keychain.updated_at, keychain.is_deleted, keychain.deleted_at, keychain.synced_at FROM keychain WHERE synced_at IS NULL OR synced_at < updated_at") {
      cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9)!!,
      cursor.getLong(10),
      cursor.getLong(11)
    )
  }

  public fun selectDirty(): Query<Keychain> = selectDirty { id, name, icon, color, wrapped_key, kdf,
      order_index, created_at, updated_at, is_deleted, deleted_at, synced_at ->
    Keychain(
      id,
      name,
      icon,
      color,
      wrapped_key,
      kdf,
      order_index,
      created_at,
      updated_at,
      is_deleted,
      deleted_at,
      synced_at
    )
  }

  public fun insert(
    id: String,
    name: String,
    icon: String,
    color: String,
    wrapped_key: String,
    kdf: String,
    order_index: Long,
    created_at: Long,
    updated_at: Long,
  ) {
    driver.execute(-483_200_555, """
        |INSERT INTO keychain(id, name, icon, color, wrapped_key, kdf, order_index, created_at, updated_at)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 9) {
          bindString(0, id)
          bindString(1, name)
          bindString(2, icon)
          bindString(3, color)
          bindString(4, wrapped_key)
          bindString(5, kdf)
          bindLong(6, order_index)
          bindLong(7, created_at)
          bindLong(8, updated_at)
        }
    notifyQueries(-483_200_555) { emit ->
      emit("keychain")
    }
  }

  public fun rename(
    name: String,
    updated_at: Long,
    id: String,
  ) {
    driver.execute(-234_002_854, """UPDATE keychain SET name = ?, updated_at = ? WHERE id = ?""", 3)
        {
          bindString(0, name)
          bindLong(1, updated_at)
          bindString(2, id)
        }
    notifyQueries(-234_002_854) { emit ->
      emit("keychain")
    }
  }

  public fun rewrap(
    wrapped_key: String,
    kdf: String,
    updated_at: Long,
    id: String,
  ) {
    driver.execute(-233_718_759,
        """UPDATE keychain SET wrapped_key = ?, kdf = ?, updated_at = ? WHERE id = ?""", 4) {
          bindString(0, wrapped_key)
          bindString(1, kdf)
          bindLong(2, updated_at)
          bindString(3, id)
        }
    notifyQueries(-233_718_759) { emit ->
      emit("keychain")
    }
  }

  public fun softDelete(
    deleted_at: Long?,
    updated_at: Long,
    id: String,
  ) {
    driver.execute(-316_842_095,
        """UPDATE keychain SET is_deleted = 1, deleted_at = ?, updated_at = ? WHERE id = ?""", 3) {
          bindLong(0, deleted_at)
          bindLong(1, updated_at)
          bindString(2, id)
        }
    notifyQueries(-316_842_095) { emit ->
      emit("keychain")
    }
  }

  public fun updateFromRemote(
    name: String,
    icon: String,
    color: String,
    wrapped_key: String,
    kdf: String,
    order_index: Long,
    updated_at: Long,
    id: String,
  ) {
    driver.execute(-693_535_147, """
        |UPDATE keychain
        |SET name = ?, icon = ?, color = ?, wrapped_key = ?, kdf = ?, order_index = ?, updated_at = ?
        |WHERE id = ?
        """.trimMargin(), 8) {
          bindString(0, name)
          bindString(1, icon)
          bindString(2, color)
          bindString(3, wrapped_key)
          bindString(4, kdf)
          bindLong(5, order_index)
          bindLong(6, updated_at)
          bindString(7, id)
        }
    notifyQueries(-693_535_147) { emit ->
      emit("keychain")
    }
  }

  public fun restore(updated_at: Long, id: String) {
    driver.execute(1_341_032_178,
        """UPDATE keychain SET is_deleted = 0, deleted_at = NULL, updated_at = ? WHERE id = ?""", 2)
        {
          bindLong(0, updated_at)
          bindString(1, id)
        }
    notifyQueries(1_341_032_178) { emit ->
      emit("keychain")
    }
  }

  public fun markSynced(
    synced_at: Long?,
    id: String,
    updated_at: Long,
  ) {
    driver.execute(886_051_523,
        """UPDATE keychain SET synced_at = ? WHERE id = ? AND updated_at <= ?""", 3) {
          bindLong(0, synced_at)
          bindString(1, id)
          bindLong(2, updated_at)
        }
    notifyQueries(886_051_523) { emit ->
      emit("keychain")
    }
  }

  private inner class SelectByIdAnyStateQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("keychain", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("keychain", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_023_291_345,
        """SELECT keychain.id, keychain.name, keychain.icon, keychain.color, keychain.wrapped_key, keychain.kdf, keychain.order_index, keychain.created_at, keychain.updated_at, keychain.is_deleted, keychain.deleted_at, keychain.synced_at FROM keychain WHERE id = ?""",
        mapper, 1) {
      bindString(0, id)
    }

    override fun toString(): String = "Keychain.sq:selectByIdAnyState"
  }
}
